import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { gameServerApi } from '../../services'
import { State, GameServer, SystemMetrics } from './types'

const initialState: State = {
  servers: [],
  metrics: {
    cpu_usage: 0,
    memory_usage: 0,
    disk_usage: 0,
    active_servers: 0,
    total_players: 0,
  },
  loading: false,
  error: null,
  lastUpdated: null,
}

// Async thunks for API calls
export const fetchServers = createAsyncThunk(
  'gameServers/fetchServers',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🔄 Fetching servers from Redux thunk...')
      const servers = await gameServerApi.getGameServers()
      return servers
    } catch (error: any) {
      console.error('❌ Error fetching servers:', error)
      return rejectWithValue(error.message || 'Failed to fetch servers')
    }
  }
)

export const fetchMetrics = createAsyncThunk(
  'gameServers/fetchMetrics',
  async (_, { rejectWithValue }) => {
    try {
      const metrics = await gameServerApi.getSystemMetrics()
      return metrics
    } catch (error: any) {
      console.error('❌ Error fetching metrics:', error)
      return rejectWithValue(error.message || 'Failed to fetch metrics')
    }
  }
)

export const startServer = createAsyncThunk(
  'gameServers/startServer',
  async (serverId: string | number, { rejectWithValue, dispatch }) => {
    try {
      await gameServerApi.startGameServer(serverId)
      // Optimistically update server status
      dispatch(updateServerStatus({ serverId, status: 'starting' }))
      // Re-fetch to get actual status
      setTimeout(() => dispatch(fetchServers()), 2000)
      return serverId
    } catch (error: any) {
      console.error('❌ Error starting server:', error)
      return rejectWithValue(error.message || 'Failed to start server')
    }
  }
)

export const stopServer = createAsyncThunk(
  'gameServers/stopServer',
  async (serverId: string | number, { rejectWithValue, dispatch }) => {
    try {
      await gameServerApi.stopGameServer(serverId)
      // Optimistically update server status
      dispatch(updateServerStatus({ serverId, status: 'stopping' }))
      // Re-fetch to get actual status
      setTimeout(() => dispatch(fetchServers()), 2000)
      return serverId
    } catch (error: any) {
      console.error('❌ Error stopping server:', error)
      return rejectWithValue(error.message || 'Failed to stop server')
    }
  }
)

export const restartServer = createAsyncThunk(
  'gameServers/restartServer',
  async (serverId: string | number, { rejectWithValue, dispatch }) => {
    try {
      await gameServerApi.restartGameServer(serverId)
      // Optimistically update server status
      dispatch(updateServerStatus({ serverId, status: 'restarting' }))
      // Re-fetch to get actual status
      setTimeout(() => dispatch(fetchServers()), 2000)
      return serverId
    } catch (error: any) {
      console.error('❌ Error restarting server:', error)
      return rejectWithValue(error.message || 'Failed to restart server')
    }
  }
)

export const deleteServer = createAsyncThunk(
  'gameServers/deleteServer',
  async (serverId: string | number, { rejectWithValue, dispatch }) => {
    try {
      await gameServerApi.deleteGameServer(serverId)
      // Optimistically update server status
      dispatch(updateServerStatus({ serverId, status: 'deleting' }))
      // Re-fetch to get updated list
      setTimeout(() => dispatch(fetchServers()), 2000)
      return serverId
    } catch (error: any) {
      console.error('❌ Error deleting server:', error)
      return rejectWithValue(error.message || 'Failed to delete server')
    }
  }
)

const gameServersSlice = createSlice({
  name: 'gameServers',
  initialState,
  reducers: {
    // Update a specific server's status optimistically
    updateServerStatus: (
      state,
      action: PayloadAction<{ serverId: string | number; status: GameServer['status'] }>
    ) => {
      const { serverId, status } = action.payload
      const server = state.servers.find(
        (s) => s.id === serverId || s._id === serverId
      )
      if (server) {
        server.status = status
      }
    },
    // Update a specific server (for live updates)
    updateServer: (state, action: PayloadAction<GameServer>) => {
      const index = state.servers.findIndex(
        (s) => s.id === action.payload.id || s._id === action.payload._id
      )
      if (index !== -1) {
        state.servers[index] = { ...state.servers[index], ...action.payload }
      }
    },
    // Clear error
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    // Fetch servers
    builder.addCase(fetchServers.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(fetchServers.fulfilled, (state, action) => {
      state.loading = false

      // Smart merge: preserve transitional states if they're in progress
      // This prevents false "stopped" states when the server is actually starting
      const newServers = action.payload.map((newServer: GameServer) => {
        const existingServer = state.servers.find(
          (s) => s.id === newServer.id || s._id === newServer._id
        )

        if (existingServer) {
          // If existing server is in a transitional state, keep it unless enough time has passed
          const transitionalStates = ['starting', 'stopping', 'restarting', 'provisioning']
          if (transitionalStates.includes(existingServer.status)) {
            // Only override transitional status if the new status is different and stable
            if (newServer.status !== existingServer.status) {
              const stableStates = ['running', 'stopped']
              if (stableStates.includes(newServer.status)) {
                console.log(`✅ Server ${newServer.id} transitioned from ${existingServer.status} to ${newServer.status}`)
                return newServer
              }
              // Keep transitional state if new status is also transitional
              console.log(`⏳ Server ${newServer.id} still transitioning: ${existingServer.status}`)
              return existingServer
            }
          }
        }

        return newServer
      })

      state.servers = newServers
      state.lastUpdated = Date.now()
      state.error = null
    })
    builder.addCase(fetchServers.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload as string
    })

    // Fetch metrics
    builder.addCase(fetchMetrics.fulfilled, (state, action) => {
      state.metrics = action.payload
    })

    // Start server
    builder.addCase(startServer.rejected, (state, action) => {
      state.error = action.payload as string
    })

    // Stop server
    builder.addCase(stopServer.rejected, (state, action) => {
      state.error = action.payload as string
    })

    // Restart server
    builder.addCase(restartServer.rejected, (state, action) => {
      state.error = action.payload as string
    })

    // Delete server
    builder.addCase(deleteServer.rejected, (state, action) => {
      state.error = action.payload as string
    })
  },
})

export const { reducer, actions } = gameServersSlice

export const { updateServerStatus, updateServer, clearError } = actions

export default gameServersSlice
